import { constructSearchParams } from "utils/constructSearchParams";
import axiosInstance from "../utils/axiosInstance";
import { URLS } from "../utils/baseUrls";
import {
  EnemiesInfoDto,
  EnemiesOperationsDto,
  EnemyInfoDto,
  EnemyOperationsDto,
} from "api/dtos/EnemiesDto";

const getInformation = async ({ stressId, phaseId: bioId, uuid: enemyId }) => {
  try {
    const params = constructSearchParams({
      bioId,
      enemyId,
    });

    const res = await axiosInstance.get(
      `${URLS.externalRequests}/agro/stresses/${stressId}/get-syptoms/`,
      { params },
    );
    const data = EnemyInfoDto.fromApi(res.data);
    return data;
  } catch (err) {
    console.error("Error fetching enemy information:", err);
    throw err;
  }
};

const getEnemiesInformation = async (enemies) => {
  try {
    const promises = enemies.map((enemy) => getInformation(enemy));
    const dataArray = await Promise.all(promises);

    return EnemiesInfoDto.fromApi(dataArray, enemies);
  } catch (err) {
    console.error("Error fetching enemies information:", err);
    throw err;
  }
};

const getOperations = async (
  { uuid, phaseId: bioUuid },
  drugTaskId,
  activeSubstances,
) => {
  try {
    const params = constructSearchParams({
      bioUuid,
      uuid,
    });
    const res = await axiosInstance.get(
      `${URLS.externalRequests}/agro/enemies/get-operations/`,
      { params },
    );
    const data = EnemyOperationsDto.fromApi(
      res.data,
      uuid,
      drugTaskId,
      activeSubstances,
    );
    return data;
  } catch (err) {
    console.error("Error fetching enemy operations:", err);
    throw err;
  }
};

const getEnemiesOperations = async (enemies, drugTaskId, activeSubstances) => {
  try {
    const promises = enemies.map((enemy) =>
      getOperations(enemy, drugTaskId, activeSubstances),
    );
    const dataArray = await Promise.all(promises);

    return EnemiesOperationsDto.fromApi(
      dataArray,
      enemies,
      drugTaskId,
      activeSubstances,
    );
  } catch (err) {
    console.error("Error fetching enemies operations:", err);
    throw err;
  }
};

const getEnemies = async (
  enemies,
  drugTaskId,
  activeSubstances,
  pageSize,
  page,
) => {
  try {
    const nextEnemies = findNextEnemies(enemies, pageSize, page);
    const totalEnemies = enemies.length;

    const enemiesInfo = await getEnemiesInformation(nextEnemies);
    const enemiesOperations = await getEnemiesOperations(
      nextEnemies,
      drugTaskId,
      activeSubstances,
    );

    const newEnemies = nextEnemies.map((enemy) => ({
      ...enemy,
      fetchedOperations: enemiesOperations[enemy.uuid],
      fetchedInfo: enemiesInfo[enemy.uuid],
    }));

    return {
      data: newEnemies,
      nextPage: findNextPage(totalEnemies, pageSize, page),
    };
  } catch (err) {
    console.error("Error fetching enemies data:", err);
    throw err;
  }
};

const getStressInfo = ({ stressId, phaseId, uuid }) =>
  getInformation({ stressId, phaseId, uuid });

const getStressOperations = ({ phaseId, uuid }) =>
  getOperations({ uuid, phaseId });

export const enemiesApi = {
  fetchStressInfo: getStressInfo,
  fetchStressOperations: getStressOperations,
  fetchStressEnemies: getEnemies,
};

const findNextEnemies = (dataArray, pageSize, page) => {
  return pageSize
    ? dataArray.slice(page * pageSize, (page + 1) * pageSize)
    : dataArray;
};

const findNextPage = (total, pageSize, page) => {
  if (!pageSize) return false;

  const loadedEnemies = (page + 1) * pageSize;

  const hasMore = loadedEnemies < total;
  return hasMore ? page + 1 : undefined;
};
